import React, { useState, useRef, useEffect, useCallback } from 'react';

// 圖片裁切編輯器：以 cover 方式將圖片鋪滿固定比例的視窗，使用者可「縮放」與「拖曳位移」，
// 確認後輸出與預覽完全一致的裁切結果（JPEG Blob）。供商品上架等需要裁圖的情境共用。
// props:
//   file         要裁切的原始檔案（File 物件）
//   aspect       輸出寬高比（預設 4:3，對應商品卡顯示比例）
//   outputWidth  輸出圖片寬度（高度依 aspect 推算）
//   onConfirm    確認後回傳裁切完成的 Blob
//   onCancel     取消
const ImageCropEditor = ({ file, aspect = 4 / 3, outputWidth = 800, onConfirm, onCancel }) => {
  // 預覽用 object URL（以 file 為相依在 effect 內建立，見下方）
  const [previewUrl, setPreviewUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ w: 0, h: 0 });
  const [busy, setBusy] = useState(false);

  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  // 建立預覽用 object URL，並在卸載或重新掛載時釋放。以 file 為相依，
  // 確保 React 18 StrictMode「掛載→卸載→再掛載」時會重建新的 URL，
  // 避免沿用已被 revoke 的 URL 而造成破圖（ERR_FILE_NOT_FOUND）。
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // 量測視窗實際尺寸（供位移/裁切計算）；視窗大小改變時重新量測
  useEffect(() => {
    const measure = () => {
      if (viewportRef.current) {
        const rect = viewportRef.current.getBoundingClientRect();
        setView({ w: rect.width, h: rect.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // 將位移限制在「圖片始終鋪滿視窗」的範圍內，避免露出空白邊
  const clampOffset = useCallback((off, z) => {
    if (!view.w || !natural.w) return off;
    const base = Math.max(view.w / natural.w, view.h / natural.h);
    const dispW = natural.w * base * z;
    const dispH = natural.h * base * z;
    const maxX = Math.max(0, (dispW - view.w) / 2);
    const maxY = Math.max(0, (dispH - view.h) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, off.x)),
      y: Math.min(maxY, Math.max(-maxY, off.y)),
    };
  }, [view, natural]);

  // 拖曳位移：以指標移動量加上起始位移，並做邊界收斂
  const onPointerMove = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset(clampOffset({ x: d.baseX + (clientX - d.startX), y: d.baseY + (clientY - d.startY) }, zoom));
  }, [clampOffset, zoom]);

  useEffect(() => {
    const mm = (e) => onPointerMove(e.clientX, e.clientY);
    const tm = (e) => { if (e.touches[0]) onPointerMove(e.touches[0].clientX, e.touches[0].clientY); };
    const up = () => { dragRef.current = null; };
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', tm, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', up);
    };
  }, [onPointerMove]);

  const startDrag = (clientX, clientY) => {
    dragRef.current = { startX: clientX, startY: clientY, baseX: offset.x, baseY: offset.y };
  };

  // 縮放改變時同步重新收斂位移（縮小時可能需把圖片拉回邊界內）
  const handleZoom = (z) => {
    setZoom(z);
    setOffset((o) => clampOffset(o, z));
  };

  // 計算目前預覽中圖片的顯示尺寸與左上座標（render 與裁切共用同一套數學）
  const base = view.w && natural.w ? Math.max(view.w / natural.w, view.h / natural.h) : 1;
  const eff = base * zoom;
  const dispW = natural.w * eff;
  const dispH = natural.h * eff;
  const topLeftX = (view.w - dispW) / 2 + offset.x;
  const topLeftY = (view.h - dispH) / 2 + offset.y;

  // 依預覽中的相同變換，從原圖取出對應區域畫到輸出畫布，確保所見即所得
  const handleConfirm = () => {
    if (!imgRef.current || !view.w || !natural.w) return;
    setBusy(true);
    const sx = -topLeftX / eff;
    const sy = -topLeftY / eff;
    const sW = view.w / eff;
    const sH = view.h / eff;
    const outW = outputWidth;
    const outH = Math.round(outputWidth / aspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    canvas.getContext('2d').drawImage(imgRef.current, sx, sy, sW, sH, 0, 0, outW, outH);
    canvas.toBlob((blob) => {
      setBusy(false);
      if (blob) onConfirm(blob);
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="img-crop-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="img-crop-dialog">
        <h3 className="img-crop-title">調整圖片（拖曳移動位置、滑桿縮放）</h3>
        <div
          className="img-crop-viewport"
          ref={viewportRef}
          style={{ aspectRatio: String(aspect) }}
          onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
          onTouchStart={(e) => { if (e.touches[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
        >
          {previewUrl && (
            <img
              ref={imgRef}
              src={previewUrl}
              alt="預覽"
              draggable={false}
              onLoad={(e) => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
              style={{ position: 'absolute', width: dispW || 'auto', height: dispH || 'auto', left: topLeftX, top: topLeftY, userSelect: 'none' }}
            />
          )}
        </div>
        <div className="img-crop-controls">
          <label>縮放 {zoom.toFixed(1)}x</label>
          <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => handleZoom(Number(e.target.value))} />
        </div>
        <div className="btn-group" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn btn-sm btn-primary" disabled={busy || !natural.w} onClick={handleConfirm}>{busy ? '處理中...' : '確認裁切'}</button>
          <button className="btn btn-sm btn-secondary" disabled={busy} onClick={onCancel}>取消</button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropEditor;
